/**
 * «Enlace para tu cliente» (idea 9): la página PÚBLICA, de solo lectura y SIN sesión.
 *
 * Se monta desde `main.tsx` ANTES del arranque de sesión (no pasa por AuthBootstrap ni por los vigilantes de
 * `App`): quien abre el enlace no tiene cuenta. El token viaja en el fragmento (`#t=…`) y de ahí a la cabecera
 * `X-Enlace-Token`; nunca va en la ruta ni en el query. Todo se pinta como TEXTO (React escapa), sin HTML
 * suelto. Si el plano es un DXF se ve con el visor; si es IFC, con el visor 3D desde una URL local (blob).
 */
import { Fragment, Suspense, lazy, useEffect, useState } from 'react';
import { Button, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import type { EnlacePublicoRespuesta, RenglonPublico } from '@supernova/shared';
import { AvanceParaElCliente } from '../components/planos/AvanceParaElCliente.js';
import { ComoSeCalculo } from '../components/planos/ComoSeCalculo.js';
import { formatoDeMoneda } from '../lib/money.js';
import {
  EnlacePublicoError,
  bajarPlanoPublico,
  leerEnlacePublico,
  tokenDeLaUrl,
} from '../api/enlaces.js';

const PlanoViewerMlightcad = lazy(() =>
  import('../components/PlanoViewerMlightcad.js').then((m) => ({ default: m.PlanoViewerMlightcad })),
);
const IfcViewer3D = lazy(() => import('../components/IfcViewer3D.js').then((m) => ({ default: m.IfcViewer3D })));

const useStyles = makeStyles({
  pagina: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    padding: '24px 16px 48px',
  },
  contenedor: { maxWidth: '1100px', margin: '0 auto', display: 'grid', rowGap: '24px' },
  cabecera: { display: 'grid', rowGap: '4px' },
  titulo: { margin: 0, fontSize: '24px', fontWeight: 600 },
  sub: { margin: 0, color: 'var(--text-secondary)', fontSize: '14px' },
  tarjeta: {
    backgroundColor: 'var(--bg-surface)',
    border: `1px solid var(--border-subtle)`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px',
    overflowX: 'auto',
  },
  // Flex en columna: el visor 3D (IFC) toma su alto del contenedor flexible; sin esto mide 0 y no se ve el modelo.
  visor: { height: '60vh', minHeight: '320px', position: 'relative', display: 'flex', flexDirection: 'column' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    textAlign: 'left',
    padding: '8px',
    borderBottom: `1px solid var(--border-subtle)`,
    color: 'var(--text-secondary)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  td: { padding: '8px', borderBottom: `1px solid var(--border-subtle)`, verticalAlign: 'top' },
  // En teléfono la clave del concepto es lo menos útil para el cliente: se oculta para que quepan las demás columnas.
  clave: { '@media (max-width: 640px)': { display: 'none' } },
  num: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  partida: { fontWeight: 600, backgroundColor: 'var(--bg-elevated, transparent)' },
  hijo: { paddingLeft: '28px' },
  centro: { minHeight: '100vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: '24px' },
  total: { textAlign: 'right', fontWeight: 600, paddingTop: '12px' },
});

const fmt = (n: number | null | undefined, dec = 2): string =>
  n === null || n === undefined ? '' : n.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtFecha = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

type Carga =
  | { tipo: 'cargando' }
  | { tipo: 'listo'; datos: EnlacePublicoRespuesta }
  | { tipo: 'error'; mensaje: string };

function agruparPorPartida(renglones: RenglonPublico[]): { partida: string; renglones: RenglonPublico[] }[] {
  const grupos = new Map<string, RenglonPublico[]>();
  for (const r of renglones) {
    const g = grupos.get(r.partida);
    if (g) g.push(r);
    else grupos.set(r.partida, [r]);
  }
  return [...grupos.entries()].map(([partida, rs]) => ({ partida, renglones: rs }));
}

export function EnlacePublicoPage(): JSX.Element {
  const s = useStyles();
  const [token] = useState(() => tokenDeLaUrl());
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
  const [abierto, setAbierto] = useState<number | null>(null);
  const [ifcUrl, setIfcUrl] = useState<string | null>(null);
  const [errorIfc, setErrorIfc] = useState<string | null>(null);
  // Con avance en la foto, la página abre en «Avance de obra»; el MISMO visor cambia de modo (no se montan dos).
  const [vista, setVista] = useState<'avance' | 'presupuesto'>('avance');

  // No indexar ni mandar referer desde esta página.
  useEffect(() => {
    const metas: HTMLMetaElement[] = [];
    for (const [name, content] of [['robots', 'noindex, nofollow'], ['referrer', 'no-referrer']] as const) {
      const m = document.createElement('meta');
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      metas.push(m);
    }
    document.title = 'Presupuesto compartido';
    return () => metas.forEach((m) => m.remove());
  }, []);

  useEffect(() => {
    if (!token) {
      setCarga({ tipo: 'error', mensaje: new EnlacePublicoError('NO_DISPONIBLE').message });
      return;
    }
    let vivo = true;
    leerEnlacePublico(token)
      .then((datos) => vivo && setCarga({ tipo: 'listo', datos }))
      .catch((e: unknown) => {
        const err = e instanceof EnlacePublicoError ? e : new EnlacePublicoError('SERVIDOR');
        if (vivo) setCarga({ tipo: 'error', mensaje: err.message });
      });
    return () => {
      vivo = false;
    };
  }, [token]);

  // IFC: se baja con el token y se le da al visor una URL local (el visor no puede mandar cabeceras).
  const formato = carga.tipo === 'listo' ? carga.datos.plano.formato : null;
  useEffect(() => {
    if (formato !== 'ifc' || !token) return;
    let url: string | null = null;
    let vivo = true;
    const ctl = new AbortController();
    bajarPlanoPublico(token, ctl.signal)
      .then((buf) => {
        if (!vivo) return;
        url = URL.createObjectURL(new Blob([buf], { type: 'application/x-step' }));
        setIfcUrl(url);
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setErrorIfc((e instanceof EnlacePublicoError ? e : new EnlacePublicoError('SERVIDOR')).message);
      });
    return () => {
      vivo = false;
      ctl.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [formato, token]);

  if (carga.tipo === 'cargando') {
    return (
      <div className={s.centro}>
        <Spinner label="Cargando…" />
      </div>
    );
  }
  if (carga.tipo === 'error') {
    return (
      <div className={s.centro}>
        <p>{carga.mensaje}</p>
      </div>
    );
  }

  const { datos } = carga;
  // Importes con el símbolo de la moneda de la obra; sin moneda conocida, el número pelado (nunca un default).
  const { money } = formatoDeMoneda(datos.moneda);
  const conPrecios = datos.mostrarPrecios;
  const cols = conPrecios ? 7 : 5;
  const grupos = agruparPorPartida(datos.foto.renglones);

  return (
    <div className={s.pagina}>
      <div className={s.contenedor}>
        <header className={s.cabecera}>
          <h1 className={s.titulo}>{datos.obra}</h1>
          <p className={s.sub}>
            {datos.plano.nombre} · hoja del {fmtFecha(datos.fotoAt)} · disponible hasta el {fmtFecha(datos.expiraAt)}
          </p>
        </header>

        {datos.foto.avance && (
          <div role="tablist" aria-label="Qué ver" style={{ display: 'flex', gap: 8 }}>
            {(['avance', 'presupuesto'] as const).map((v) => (
              <Button
                key={v}
                role="tab"
                aria-selected={vista === v}
                appearance={vista === v ? 'primary' : 'secondary'}
                onClick={() => setVista(v)}
              >
                {v === 'avance' ? 'Avance de obra' : 'Presupuesto'}
              </Button>
            ))}
          </div>
        )}

        {datos.foto.avance && vista === 'avance' && token && (
          <section className={s.tarjeta} aria-label="Avance de obra">
            <AvanceParaElCliente
              avance={datos.foto.avance}
              renglones={datos.foto.renglones}
              formato={datos.plano.formato}
              ifcUrl={ifcUrl}
              errorIfc={errorIfc}
              cargarDxf={() => bajarPlanoPublico(token)}
            />
          </section>
        )}

        {datos.plano.formato && token && !(datos.foto.avance && vista === 'avance') && (
          <section className={s.tarjeta} aria-label="Plano">
            <div className={s.visor}>
              <Suspense fallback={<Spinner label="Cargando plano…" />}>
                {datos.plano.formato === 'dxf' && (
                  <PlanoViewerMlightcad
                    projectId=""
                    fileId=""
                    renglones={[]}
                    onSeleccion={() => {}}
                    cargarDxf={() => bajarPlanoPublico(token)}
                    sinControles
                  />
                )}
                {datos.plano.formato === 'ifc' &&
                  (ifcUrl ? <IfcViewer3D srcUrl={ifcUrl} /> : errorIfc ? <p>{errorIfc}</p> : <Spinner label="Cargando modelo…" />)}
              </Suspense>
            </div>
          </section>
        )}

        {!(datos.foto.avance && vista === 'avance') && (
        <section className={s.tarjeta} aria-label="Presupuesto">
          <table className={s.tabla}>
            <thead>
              <tr>
                <th className={`${s.th} ${s.clave}`}>Clave</th>
                <th className={s.th}>Concepto</th>
                <th className={s.th}>Unidad</th>
                <th className={`${s.th} ${s.num}`}>Cantidad</th>
                {conPrecios && <th className={`${s.th} ${s.num}`}>P. unitario</th>}
                {conPrecios && <th className={`${s.th} ${s.num}`}>Importe</th>}
                <th className={s.th}>Cómo se calculó</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <Fragment key={g.partida}>
                  <tr>
                    <td className={`${s.td} ${s.partida}`} colSpan={cols}>
                      {g.partida}
                    </td>
                  </tr>
                  {g.renglones.map((r) => (
                    <Fragment key={r.n}>
                      <tr>
                        <td className={`${s.td} ${s.clave}`}>{r.clave ?? ''}</td>
                        <td className={`${s.td} ${r.esComplemento ? s.hijo : ''}`}>{r.descripcion}</td>
                        <td className={s.td}>{r.unidad ?? ''}</td>
                        <td className={`${s.td} ${s.num}`}>{fmt(r.cantidad, r.generador?.decimales ?? 2)}</td>
                        {conPrecios && (
                          <td className={`${s.td} ${s.num}`}>
                            {r.precioUnitario == null ? <span className={s.sub}>Por definir</span> : money(r.precioUnitario)}
                          </td>
                        )}
                        {conPrecios && (
                          <td className={`${s.td} ${s.num}`}>
                            {r.importe == null ? <span className={s.sub}>Por definir</span> : money(r.importe)}
                          </td>
                        )}
                        <td className={s.td}>
                          {r.generador && (
                            <Button size="small" appearance="subtle" onClick={() => setAbierto(abierto === r.n ? null : r.n)}>
                              {abierto === r.n ? 'Ocultar' : 'Ver cómo se calculó'}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {abierto === r.n && r.generador && (
                        <tr>
                          <td className={s.td} colSpan={cols}>
                            <ComoSeCalculo g={r.generador} cantidad={r.cantidad} unidad={r.unidad} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          {conPrecios && datos.foto.total !== undefined && (
            <div className={s.total}>
              Total: {money(datos.foto.total)}
              {datos.foto.sinPrecio ? (
                <p className={s.sub} style={{ fontWeight: 400 }}>
                  {datos.foto.sinPrecio === 1
                    ? 'Hay 1 concepto con precio por definir; todavía no está incluido en este total.'
                    : `Hay ${datos.foto.sinPrecio} conceptos con precio por definir; todavía no están incluidos en este total.`}
                </p>
              ) : null}
            </div>
          )}
        </section>
        )}
      </div>
    </div>
  );
}
